import { ref, type Ref } from 'vue'
import type { DragPayload, DragSource, DragItemType } from './types'
import type { GridFieldDragType } from '@/features/Inventory/ObjectTypePassportGrid/model/types'

export const PB_DND_MIME = 'application/x-passport-builder-v2+json'
const MIME = PB_DND_MIME

/** Builds a {@link DragPayload} with a matching `payloadJson` for `dataTransfer`. */
export function createDragPayload(
  source: DragSource,
  itemType: DragItemType,
  field: GridFieldDragType,
): DragPayload {
  const core = { source, itemType, field }
  return {
    ...core,
    payloadJson: JSON.stringify(core),
  }
}

/** Parses drag data from a `dragstart` / `drop` event. */
export function parseDragTransfer(event: DragEvent): DragPayload | null {
  const dt = event.dataTransfer
  const raw =
    dt?.getData(PB_DND_MIME) ||
    dt?.getData('application/json') ||
    dt?.getData('text/plain')
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as { source?: DragSource; itemType?: DragItemType; field?: GridFieldDragType }
    if (!parsed.field || !parsed.source || !parsed.itemType) return null
    return createDragPayload(parsed.source, parsed.itemType, parsed.field)
  } catch {
    return null
  }
}

/**
 * HTML5 drag-and-drop state for the passport grid.
 * Call `startDrag` from `dragstart`, `endDrag` from `dragend` / successful `drop`.
 */
export function useDragDrop() {
  const dragData: Ref<DragPayload | null> = ref(null)
  const dropTargetRow: Ref<number | null> = ref(null)

  const startDrag = (payload: DragPayload, event?: DragEvent) => {
    dragData.value = payload
    if (event?.dataTransfer) {
      const dt = event.dataTransfer
      // copyMove: palette/table targets use dropEffect 'copy'; grid row moves use 'move'.
      dt.effectAllowed = 'copyMove'
      dt.setData(MIME, payload.payloadJson)
      dt.setData('application/json', payload.payloadJson)
      dt.setData('text/plain', payload.payloadJson)
    }
  }

  const endDrag = () => {
    dragData.value = null
    dropTargetRow.value = null
  }

  const onDragOver = (event: DragEvent, row?: number) => {
    event.preventDefault()
    if (row !== undefined) dropTargetRow.value = row
  }

  const onDragLeave = (event: DragEvent, options?: { clearRow?: boolean }) => {
    const clear = options?.clearRow ?? false
    if (clear) dropTargetRow.value = null
    void event
  }

  return {
    dragData,
    dropTargetRow,
    startDrag,
    endDrag,
    onDragOver,
    onDragLeave,
  }
}
